/**
 * Utilitário para compartilhamento de sessão entre aplicações FotUp
 * Permite que o usuário logado no frontend seja automaticamente logado no uploader
 */

import { getNormalizedApiBase } from '@/utils/apiUrlHelper';
import { getUploaderUrlWithAuthCode, getBackendApiUrl } from '@/config/services';

const API_BASE = getNormalizedApiBase();

export interface SharedSession {
  token: string;
  refreshToken: string;
  user: {
    id: string;
    email: string;
    fullName?: string;
    name?: string;
    role: 'admin' | 'photographer' | 'athlete';
    isApproved?: boolean;
  };
  timestamp: number;
  expiresAt: number;
}

export class SessionManager {
  private static readonly STORAGE_KEY = 'fotup_shared_session';
  private static readonly SESSION_DURATION = 24 * 60 * 60 * 1000; // 24 horas

  /**
   * Salva a sessão atual no localStorage para compartilhamento
   */
  static saveSession(user: any, token: string, refreshToken: string): void {
    const session: SharedSession = {
      token,
      refreshToken,
      user: {
        id: user.id,
        email: user.email,
        fullName: user.fullName,
        name: user.name,
        role: user.role,
        isApproved: user.isApproved
      },
      timestamp: Date.now(),
      expiresAt: Date.now() + this.SESSION_DURATION
    };

    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(session));

    // localStorage.setItem('fotup_token', token);
    // localStorage.setItem('fotup_refresh_token', refreshToken);
    
    // Notificar outras abas sobre a mudança de sessão
    this.broadcastSessionChange(session);
  }

  /**
   * Recupera a sessão compartilhada do localStorage
   */
  static getSharedSession(): SharedSession | null {
    try {
      
      const sessionData = localStorage.getItem(this.STORAGE_KEY);
      
      
      if (!sessionData) {
        
        return null;
      }

      const session: SharedSession = JSON.parse(sessionData);
      
      // Verificar se a sessão não expirou
      if (Date.now() > session.expiresAt) {
        
        this.clearSession();
        return null;
      }

      
      return session;
    } catch (error) {
      console.error('❌ Erro ao recuperar sessão compartilhada:', error);
      return null;
    }
  }

  /**
   * Limpa a sessão compartilhada
   */
  static clearSession(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem('fotup_token');
    localStorage.removeItem('fotup_refresh_token');
    this.broadcastSessionChange(null);
  }

  /**
   * Verifica se há uma sessão válida compartilhada
   */
  static hasValidSession(): boolean {
    return this.getSharedSession() !== null;
  }

  /**
   * Converte sessão compartilhada para formato Supabase
   */
  static convertToSupabaseSession(session: SharedSession): any {
    return {
      access_token: session.token,
      refresh_token: session.refreshToken,
      user: {
        id: session.user.id,
        email: session.user.email,
        user_metadata: {
          full_name: session.user.fullName || session.user.name
        }
      },
      expires_at: Math.floor(session.expiresAt / 1000)
    };
  }

  /**
   * Notifica outras abas sobre mudanças na sessão
   */
  private static broadcastSessionChange(session: SharedSession | null): void {
    try {
      // Usar BroadcastChannel se disponível (navegadores modernos)
      if (typeof BroadcastChannel !== 'undefined') {
        const channel = new BroadcastChannel('fotup_session');
        channel.postMessage({
          type: 'SESSION_CHANGE',
          session
        });
        channel.close();
      }

      // Fallback: usar localStorage event (para navegadores mais antigos)
      window.dispatchEvent(new StorageEvent('storage', {
        key: this.STORAGE_KEY,
        newValue: session ? JSON.stringify(session) : null,
        oldValue: localStorage.getItem(this.STORAGE_KEY),
        storageArea: localStorage
      }));
    } catch (error) {
      console.error('Erro ao notificar mudança de sessão:', error);
    }
  }

  /**
   * Escuta mudanças de sessão de outras abas
   */
  static onSessionChange(callback: (session: SharedSession | null) => void): () => void {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === this.STORAGE_KEY) {
        const session = event.newValue ? JSON.parse(event.newValue) : null;
        callback(session);
      }
    };

    const handleBroadcast = (event: MessageEvent) => {
      if (event.data?.type === 'SESSION_CHANGE') {
        callback(event.data.session);
      }
    };

    // Escutar eventos de storage
    window.addEventListener('storage', handleStorageChange);

    // Escutar BroadcastChannel se disponível
    let broadcastChannel: BroadcastChannel | null = null;
    if (typeof BroadcastChannel !== 'undefined') {
      broadcastChannel = new BroadcastChannel('fotup_session');
      broadcastChannel.addEventListener('message', handleBroadcast);
    }

    // Retornar função de cleanup
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      if (broadcastChannel) {
        broadcastChannel.close();
      }
    };
  }

  /**
   * Obtém o token atual da sessão compartilhada
   * Centraliza o acesso ao token em todo o sistema
   */
  static getToken(): string | null {
    const session = this.getSharedSession();
    const token = session?.token || null;
    return token;
  }

  /**
   * Obtém o refresh token atual da sessão compartilhada
   */
  static getRefreshToken(): string | null {
    const session = this.getSharedSession();
    return session?.refreshToken || null;
  }

  /**
   * Salva tokens diretamente no localStorage (compatibilidade com código legado)
   * @deprecated Use saveSession() para nova implementação. saveSession() ja salva os tokens no localStorage
   */
  static saveTokensDirectly(accessToken: string, refreshToken: string): void {

    var session: SharedSession | null = this.getSharedSession();

    if (session) {
      session.token = accessToken;
      session.refreshToken = refreshToken;
      this.saveSession(session.user, session.token, session.refreshToken);
    }

    // localStorage.setItem('fotup_token', accessToken);
    // localStorage.setItem('fotup_refresh_token', refreshToken);
  }

  /**
   * Limpa tokens diretamente do localStorage (compatibilidade com código legado)
   * @deprecated Use clearSession() para nova implementação
   */
  static clearTokensDirectly(): void {
    this.clearSession();
    // localStorage.removeItem('fotup_token');
    // localStorage.removeItem('fotup_refresh_token');
  }

  /**
   * Abre o uploader em nova aba com AuthCode
   */
  static async openUploader(): Promise<void> {
    try {
      // Gerar AuthCode no backend
      const token = this.getToken();
      if (!token) {
        console.error('❌ Token não encontrado para gerar AuthCode');
        return;
      }

      const backendUrl = getBackendApiUrl();
      console.log('🔐 Gerando AuthCode via:', `${backendUrl}/api/auth/generate-authcode`);
      
      const response = await fetch(`${backendUrl}/api/auth/generate-authcode`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📡 Resposta do backend:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Erro na resposta:', errorText);
        throw new Error(`Falha ao gerar AuthCode: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ AuthCode gerado:', data);
      
      const authCode = data.data.authCode;
      
      // Construir URL do uploader com AuthCode usando configuração
      const uploaderUrl = getUploaderUrlWithAuthCode(authCode);
      console.log('🌐 Abrindo uploader em:', uploaderUrl);
      
      // Abrir em nova aba
      const newWindow = window.open(uploaderUrl, '_blank', 'noopener,noreferrer');
      
      if (!newWindow) {
        console.error('❌ Não foi possível abrir o uploader. Verifique se pop-ups estão bloqueados.');
        return;
      }

      console.log('✅ Uploader aberto com AuthCode:', authCode.substring(0, 8) + '...');
      
    } catch (error) {
      console.error('❌ Erro ao abrir uploader:', error);
    }
  }
}
